<template>
    <div :class="['tab-group', `tab-group--${variant}`, { 'tab-group--stretched': stretched }]">
        <button
            v-for="(tab, index) in tabs"
            :key="index"
            @click="onTabClick(tab)"
            class="tab-group__tab"
            :class="{
                'tab-group__tab--active': modelValue === tab.value,
            }"
            v-show="!tab.disabled"
        >
            <span :data-text="tab.text">{{ tab.text }}</span>
        </button>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import type { TabValue as _Tab } from "@/shared/model/tab-group/tab-group";
import { analyticsPageObj } from "@/shared/composables/analytics/use-general-entities";
import { Constants } from "@/shared/model/enums/analytics/constants";

// Define Props
type TabGroupVariant = "s" | "l" | "l-inverse";
const props = withDefaults(
    defineProps<{
        tabs: _Tab[];
        variant?: TabGroupVariant;
        stretched?: boolean;
        disableAnalytics?: boolean;
        resetOnRouteChange?: boolean;
        autoselectFirstEnabledTab?: boolean;
    }>(),
    {
        variant: "l",
        stretched: false,
        disableAnalytics: false,
        resetOnRouteChange: true,
        autoselectFirstEnabledTab: true,
    },
);

const model = defineModel<string>();
const router = useRouter();

const onTabClick = (tab: _Tab) => {
    model.value = tab.value;
};

// Watch modelValue
watch(
    () => model.value,
    (newValue) => {
        if (!props.disableAnalytics) {
            analyticsPageObj.kind = props.tabs.find((tab) => tab.value === newValue)?.text || Constants.NotApplied;
        }
    },
    { immediate: true },
);

// Handle Route Change
router.afterEach(() => {
    if (!props.disableAnalytics && props.tabs.length > 0) {
        if (props.resetOnRouteChange) {
            model.value = props.tabs[0].value;
            analyticsPageObj.kind = props.tabs[0].text || Constants.NotApplied;
        }
    }
});

onMounted(() => {
    // Handle initial route with activeTab query parameter which was stored in window.query and deleted before entering the route
    const tabValue = window?.query?.activeTab ?? model.value;
    const validTab = props.tabs.some((tab) => tab.value === tabValue);

    if (validTab) {
        model.value = tabValue;
    } else if (props.autoselectFirstEnabledTab) {
        // If tab doesn't exist or invalid query param use default
        model.value = props.tabs[0].value;
    }
    // Remove activeTab from window.query
    if (window?.query?.activeTab) {
        window.query.activeTab = undefined;
    }
});

onUnmounted(() => {
    analyticsPageObj.kind = Constants.NotApplied;
});
</script>
